import { MediaUrl } from '@/types'
import { TvChannelWithEpg } from '@setplex/tria-api'
import { combine, createEvent, createStore, sample } from 'effector'
import { epgDatesModel } from '~/entities/epg/dates'
import { epgProgramsModel } from '~/entities/epg/programs'
import { $route } from '~/processes/navigation'
import { ROUTE_NAME } from '~/shared/constants'
import { getCurrentProgram } from '~/shared/helpers/epg'

type CatchupInterval = { startTime: number; endTime: number } | null
type Timeshift = { enabled: boolean; shift: number } | null
type PlayingRewindData = {
  isRewinded: boolean
  programStartMs: number | null
}

const $isEpgPage = $route.map(
  (route) => route?.routeName === ROUTE_NAME.LIVE_CATEGORIES
)

const pickByRoute = <T>(isEpgPage: boolean, epg: T, live: T) =>
  isEpgPage ? epg : live

export const resetEpgPlayingRewindData = createEvent()
export const resetLivePlayingRewindData = createEvent()

export const $epgActiveChannelWithFormattedEPG =
  createStore<TvChannelWithEpg | null>(null)
export const $liveActiveChannelWithFormattedEPG =
  createStore<TvChannelWithEpg | null>(null)
export const $epgCatchupInterval = createStore<CatchupInterval>(null)
export const $liveCatchupInterval = createStore<CatchupInterval>(null)
export const $epgTimeshift = createStore<Timeshift>(null)
export const $liveTimeshift = createStore<Timeshift>(null)
export const $epgUrl = createStore<MediaUrl | null>(null)
export const $liveUrl = createStore<MediaUrl | null>(null)

const setEpgPlayingRewindData = createEvent<PlayingRewindData>()
const setLivePlayingRewindData = createEvent<PlayingRewindData>()

export const updateProgramData = createEvent()

const $epgPlayingRewindData = createStore<PlayingRewindData>({
  isRewinded: false,
  programStartMs: null,
})
  .reset(resetEpgPlayingRewindData)
  .on(setEpgPlayingRewindData, (_, rewindData) => rewindData)
  .on(updateProgramData, (state) => ({ ...state }))

const $livePlayingRewindData = createStore<PlayingRewindData>({
  isRewinded: false,
  programStartMs: null,
})
  .reset(resetLivePlayingRewindData)
  .on(setLivePlayingRewindData, (_, rewindData) => rewindData)
  .on(updateProgramData, (state) => ({ ...state }))

export const $activeChannelWithFormattedEPG = combine(
  {
    isEpgPage: $isEpgPage,
    epg: $epgActiveChannelWithFormattedEPG,
    live: $liveActiveChannelWithFormattedEPG,
  },
  ({ isEpgPage, epg, live }) => pickByRoute(isEpgPage, epg, live)
)

export const $catchupInterval = combine(
  {
    isEpgPage: $isEpgPage,
    epg: $epgCatchupInterval,
    live: $liveCatchupInterval,
  },
  ({ isEpgPage, epg, live }) => pickByRoute(isEpgPage, epg, live)
)

export const $timeshift = combine(
  {
    isEpgPage: $isEpgPage,
    epg: $epgTimeshift,
    live: $liveTimeshift,
  },
  ({ isEpgPage, epg, live }) => pickByRoute(isEpgPage, epg, live)
)

export const $url = combine(
  {
    isEpgPage: $isEpgPage,
    epg: $epgUrl,
    live: $liveUrl,
  },
  ({ isEpgPage, epg, live }) => pickByRoute(isEpgPage, epg, live)
)

export const $playingRewindData = combine(
  {
    isEpgPage: $isEpgPage,
    epg: $epgPlayingRewindData,
    live: $livePlayingRewindData,
  },
  ({ isEpgPage, epg, live }) => pickByRoute(isEpgPage, epg, live)
)

const $epgCurrentProgram = combine(
  {
    activeChannelWithFormattedEPG: $epgActiveChannelWithFormattedEPG,
    epgIsReady: epgProgramsModel.$epgsEffectIsReady,
    catchupInterval: $epgCatchupInterval,
    today: epgDatesModel.$today,
    playingRewindData: $epgPlayingRewindData,
    timeshift: $epgTimeshift,
  },
  ({
    activeChannelWithFormattedEPG,
    epgIsReady,
    catchupInterval,
    today,
    playingRewindData,
    timeshift,
  }) =>
    getCurrentProgram({
      activeChannelWithFormattedEPG,
      epgIsReady,
      catchupInterval,
      today,
      playingRewindData,
      timeshift,
    })
)

const $liveCurrentProgram = combine(
  {
    activeChannelWithFormattedEPG: $liveActiveChannelWithFormattedEPG,
    epgIsReady: epgProgramsModel.$epgsEffectIsReady,
    catchupInterval: $liveCatchupInterval,
    today: epgDatesModel.$today,
    playingRewindData: $livePlayingRewindData,
    timeshift: $liveTimeshift,
  },
  ({
    activeChannelWithFormattedEPG,
    epgIsReady,
    catchupInterval,
    today,
    playingRewindData,
    timeshift,
  }) =>
    getCurrentProgram({
      activeChannelWithFormattedEPG,
      epgIsReady,
      catchupInterval,
      today,
      playingRewindData,
      timeshift,
    })
)

export const $currentProgram = combine(
  {
    isEpgPage: $isEpgPage,
    epg: $epgCurrentProgram,
    live: $liveCurrentProgram,
  },
  ({ isEpgPage, epg, live }) => pickByRoute(isEpgPage, epg, live)
)

sample({
  clock: $epgUrl,
  source: $epgCurrentProgram,
  fn: (currentProgram, url) => ({
    isRewinded: Boolean(url?.timeShiftSec),
    programStartMs: currentProgram?.actualStartMs || 0,
  }),
  target: setEpgPlayingRewindData,
})

sample({
  clock: $liveUrl,
  source: $liveCurrentProgram,
  fn: (currentProgram, url) => ({
    isRewinded: Boolean(url?.timeShiftSec),
    programStartMs: currentProgram?.actualStartMs || 0,
  }),
  target: setLivePlayingRewindData,
})
